import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Building2 } from "lucide-react";

interface Gym {
  id: string;
  name: string;
  logo_url: string | null;
  address: string;
}

interface GymSelectorProps {
  onSelect: (gymId: string) => Promise<boolean>;
}

export const GymSelector = ({ onSelect }: GymSelectorProps) => {
  const [gyms, setGyms] = useState<Gym[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const loadGyms = async () => {
      try {
        const { data, error } = await supabase
          .from('gyms')
          .select('id, name, logo_url, address')
          .order('name');

        if (error) throw error;
        setGyms(data || []);
      } catch (err) {
        console.error("Error loading gyms:", err);
      } finally {
        setIsLoading(false);
      }
    };

    loadGyms();
  }, []);

  const handleSelect = async () => {
    if (!selectedId) return;
    
    setIsSubmitting(true);
    const success = await onSelect(selectedId);
    if (!success) {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Selecciona tu Gimnasio</CardTitle>
          <p className="text-muted-foreground mt-2">
            Elige el gimnasio donde trabajarás como monitor
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 max-h-[400px] overflow-y-auto">
            {gyms.map((gym) => (
              <button
                key={gym.id}
                onClick={() => setSelectedId(gym.id)}
                disabled={isSubmitting}
                className={`flex items-center gap-4 p-4 rounded-lg border transition-all text-left ${
                  selectedId === gym.id
                    ? "border-primary bg-primary/10"
                    : "border-border hover:border-primary/50 hover:bg-muted/50"
                }`}
              >
                {gym.logo_url ? (
                  <img
                    src={gym.logo_url}
                    alt={gym.name}
                    className="h-12 w-12 rounded-lg object-cover"
                  />
                ) : (
                  <div className="h-12 w-12 rounded-lg bg-muted flex items-center justify-center">
                    <Building2 className="h-6 w-6 text-muted-foreground" />
                  </div>
                )}
                <div className="flex-1">
                  <h3 className="font-medium">{gym.name}</h3>
                  <p className="text-sm text-muted-foreground">{gym.address}</p>
                </div>
              </button>
            ))}
          </div>

          {gyms.length === 0 && (
            <p className="text-center text-muted-foreground py-8">
              No hay gimnasios disponibles
            </p>
          )}

          <Button
            onClick={handleSelect}
            disabled={!selectedId || isSubmitting}
            className="w-full"
            size="lg"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Asignando...
              </>
            ) : (
              "Confirmar Selección"
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};
